import { useCallback, useMemo } from "react";
import { useTran } from "@/hooks";

import UserActions from "../components/UserActions";
import FollowUpCell from "../components/FollowUpCell";
import { useUserListDerived } from "./useUserListDerived";
import { useUserStore } from "./useUserStore";
import { resolveRoleLabel } from "../utils/user-role-options";

export const useUserListColumns = ({ onViewUser, onDeleteUser } = {}) => {
  const { t } = useTran();
  const { filters, setFilters, resetFilters, setPage } = useUserStore();
  const { isAdminRole } = useUserListDerived();

  const handleUsernameFilterChange = useCallback(
    (value) => {
      setFilters({ username: value });
      setPage(1);
    },
    [setFilters, setPage],
  );

  const handleEmailFilterChange = useCallback(
    (value) => {
      setFilters({ email: value });
      setPage(1);
    },
    [setFilters, setPage],
  );

  const handleCompanyFilterChange = useCallback(
    (value) => {
      setFilters({ company: value });
      setPage(1);
    },
    [setFilters, setPage],
  );

  const handleClearFilters = useCallback(() => {
    resetFilters();
    setPage(1);
  }, [resetFilters, setPage]);

  const organizationColumnLabel = isAdminRole
    ? t("Company")
    : t("Organization");
  const organizationSortKey = isAdminRole ? "company" : "organization";
  const organizationFilterPlaceholder = isAdminRole
    ? t("Filter by company")
    : t("Filter by organization");

  return useMemo(() => {
    const baseColumns = [
      {
        key: "username",
        header: t("Display Name"),
        sortable: true,
        sortKey: "username",
        render: (user) => (
          <div className="flex flex-col">
            <span className="font-medium text-foreground">
              {user.username || t("Not available")}
            </span>
            {user.onlineStatus === "online" ? (
              <span className="text-xs text-emerald-600">{t("Online")}</span>
            ) : user.lastActiveAgo ? (
              <span className="text-xs text-muted-foreground">
                {t("Last active {{time}}", { time: user.lastActiveAgo })}
              </span>
            ) : (
              <span className="text-xs text-amber-600">{t("Offline")}</span>
            )}
          </div>
        ),
        filter: {
          type: "text",
          value: filters.username,
          onChange: handleUsernameFilterChange,
          placeholder: t("Filter by display name"),
        },
      },
      {
        key: "email",
        header: t("Email"),
        sortable: true,
        sortKey: "email",
        render: (user) => user.email || t("Not available"),
        filter: {
          type: "text",
          value: filters.email,
          onChange: handleEmailFilterChange,
          placeholder: t("Filter by email"),
        },
      },
      {
        key: "role",
        header: t("Role"),
        sortable: true,
        sortKey: "role",
        render: (user) => resolveRoleLabel(user.role, t) || t("Not available"),
      },
      {
        key: "organization",
        header: organizationColumnLabel,
        sortable: true,
        sortKey: organizationSortKey,
        render: (user) => {
          const value = isAdminRole
            ? user.company
            : user.organizationUnitName || user.organizationUnitId;
          return value || t("Not assigned");
        },
        filter: {
          type: "text",
          value: filters.company,
          onChange: handleCompanyFilterChange,
          placeholder: organizationFilterPlaceholder,
        },
      },
      ...(isAdminRole
        ? [
            {
              key: "incharge",
              header: t("Incharge"),
              width: "14%",
              render: (user) => <FollowUpCell user={user} />,
            },
          ]
        : []),
      {
        key: "actions",
        header: t("Actions"),
        align: "right",
        width: "10%",
        filter: {
          render: (
            <button
              type="button"
              onClick={handleClearFilters}
              className="text-sm text-destructive hover:text-destructive/80"
            >
              {t("Clear All Filters")}
            </button>
          ),
        },
        render: (user) => {
          return (
            <div className="flex justify-end">
              <UserActions
                user={user}
                onViewUser={onViewUser}
                onDeleteUser={onDeleteUser}
              />
            </div>
          );
        },
      },
    ];

    return baseColumns;
  }, [
    filters.company,
    filters.email,
    filters.username,
    handleClearFilters,
    handleCompanyFilterChange,
    handleEmailFilterChange,
    handleUsernameFilterChange,
    isAdminRole,
    organizationColumnLabel,
    organizationFilterPlaceholder,
    organizationSortKey,
    t,
    onViewUser,
    onDeleteUser,
  ]);
};
